/**
 * GENERATED from contract/floor-art.json by npm run generate:floor-art -w contract — do not edit by hand.
 *
 * What each place on a floor plan looks like from above, shared with the register so the two
 * screens draw one room. Read `contract/floor-art.json` — it carries the reasoning, and it is
 * the only file to change.
 */

export type SpotShape = 'rect' | 'square' | 'round' | 'hex' | 'stool' | 'lounger';

export interface ArtPaths {
  /** The outline itself. */
  path: string;
  /** Secondary strokes drawn inside — a stool's footrest, a lounger's backrest. */
  detail?: string;
}

export interface ArtEntry {
  /** Which of the two drawings was drawn first; a square footprint keeps it. */
  authored: 'wide' | 'tall';
  wide: ArtPaths;
  tall: ArtPaths;
}

export interface Material {
  fill: string;
  edge: string;
  text: string;
}

/** The kinds whose silhouette comes from the KIND and never from the shape column. */
export const DECOR_KINDS: readonly string[] = ['wall', 'counter', 'dock', 'tree', 'plant', 'stairs', 'sign'];

/** The kinds a bar joins end to end and at corners. */
export const RUN_KINDS: readonly string[] = ['wall', 'counter', 'dock'];

/** The colors each kind is drawn in. `furniture` is the fallback and covers every seat. */
export const MATERIALS: Record<string, Material> = {
  furniture: { fill: '#ffffff', edge: '#7c7567', text: '#20251f' },
  wall: { fill: '#d9d3c6', edge: '#a8a091', text: '#5d5647' },
  counter: { fill: '#e3d7c0', edge: '#b3a68c', text: '#5d5647' },
  dock: { fill: '#e0d3bb', edge: '#a89478', text: '#5d5647' },
  tree: { fill: '#dbe9d6', edge: '#7fa877', text: '#3f5c3a' },
  plant: { fill: '#dbe9d6', edge: '#7fa877', text: '#3f5c3a' },
  stairs: { fill: '#ded9cf', edge: '#a49c8c', text: '#5d5647' },
  sign: { fill: '#efe6d6', edge: '#b0a48c', text: '#5d5647' },
};

export function materialFor(kind: string): Material {
  return MATERIALS[kind] ?? MATERIALS.furniture!;
}

/** Corner rounding for the shapes a plain box can draw. Never consulted for a silhouette. */
export function cssRadius(shape: string): string | number {
  if (shape === 'round') return '50%';
  if (shape === 'square') return 10;
  return 8;
}

export const SHAPE_ART: Record<string, ArtEntry> = {
  hex: {authored: 'wide', wide: {path: 'M25 2 H75 L98 50 L75 98 H25 L2 50 Z'}, tall: {path: 'M98 25 V75 L50 98 L2 75 V25 L50 2 Z'}},
  stool: {authored: 'wide', wide: {path: 'M50 50 m-46 0 a46 46 0 1 0 92 0 a46 46 0 1 0 -92 0', detail: 'M50 50 m-22 0 a22 22 0 1 0 44 0 a22 22 0 1 0 -44 0'}, tall: {path: 'M50 50 m-46 0 a46 46 0 1 0 92 0 a46 46 0 1 0 -92 0', detail: 'M50 50 m-22 0 a22 22 0 1 0 44 0 a22 22 0 1 0 -44 0'}},
  lounger: {authored: 'tall', wide: {path: 'M96 22 V78 A18 18 0 0 1 78 96 H18 A14 14 0 0 1 4 82 V18 A14 14 0 0 1 18 4 H78 A18 18 0 0 1 96 22 Z', detail: 'M70 14 V86'}, tall: {path: 'M22 4 H78 A18 18 0 0 1 96 22 V82 A14 14 0 0 1 82 96 H18 A14 14 0 0 1 4 82 V22 A18 18 0 0 1 22 4 Z', detail: 'M14 30 H86'}},
};

export const DECOR_ART: Record<string, ArtEntry> = {
  tree: {authored: 'wide', wide: {path: 'M96 50 Q66.6 56.9 82.5 82.5 Q56.9 66.6 50 96 Q43.1 66.6 17.5 82.5 Q33.4 56.9 4 50 Q33.4 43.1 17.5 17.5 Q43.1 33.4 50 4 Q56.9 33.4 82.5 17.5 Q66.6 43.1 96 50 Z', detail: 'M50 50 m-9 0 a9 9 0 1 0 18 0 a9 9 0 1 0 -18 0'}, tall: {path: 'M96 50 Q66.6 56.9 82.5 82.5 Q56.9 66.6 50 96 Q43.1 66.6 17.5 82.5 Q33.4 56.9 4 50 Q33.4 43.1 17.5 17.5 Q43.1 33.4 50 4 Q56.9 33.4 82.5 17.5 Q66.6 43.1 96 50 Z', detail: 'M50 50 m-9 0 a9 9 0 1 0 18 0 a9 9 0 1 0 -18 0'}},
  plant: {authored: 'wide', wide: {path: 'M2 2 H98 V98 H2 Z', detail: 'M50 50 m-26 0 a26 26 0 1 0 52 0 a26 26 0 1 0 -52 0'}, tall: {path: 'M2 2 H98 V98 H2 Z', detail: 'M50 50 m-26 0 a26 26 0 1 0 52 0 a26 26 0 1 0 -52 0'}},
  sign: {authored: 'wide', wide: {path: 'M2 2 H72 L98 50 L72 98 H2 Z', detail: 'M14 26 V74'}, tall: {path: 'M2 2 H98 V72 L50 98 L2 72 Z', detail: 'M26 14 H74'}},
  stairs: {authored: 'wide', wide: {path: 'M2 2 H18.8 V98 H2 Z M21.8 2 H38.6 V98 H21.8 Z M41.6 2 H58.4 V98 H41.6 Z M61.4 2 H78.2 V98 H61.4 Z M81.2 2 H98 V98 H81.2 Z'}, tall: {path: 'M2 2 H98 V18.8 H2 Z M2 21.8 H98 V38.6 H2 Z M2 41.6 H98 V58.4 H2 Z M2 61.4 H98 V78.2 H2 Z M2 81.2 H98 V98 H2 Z'}},
  wall: {authored: 'wide', wide: {path: 'M0 0 H100 V100 H0 Z', detail: 'M20 2 V98 M40 2 V98 M60 2 V98 M80 2 V98'}, tall: {path: 'M0 0 H100 V100 H0 Z', detail: 'M2 20 H98 M2 40 H98 M2 60 H98 M2 80 H98'}},
  counter: {authored: 'wide', wide: {path: 'M0 0 H100 V100 H0 Z', detail: 'M2 70 H98 M2 86 H98'}, tall: {path: 'M0 0 H100 V100 H0 Z', detail: 'M70 2 V98 M86 2 V98'}},
  dock: {authored: 'wide', wide: {path: 'M0 0 H100 V100 H0 Z', detail: 'M14 2 V98 M26 2 V98 M38 2 V98 M50 2 V98 M62 2 V98 M74 2 V98 M86 2 V98 M2 12 H98 M2 88 H98'}, tall: {path: 'M0 0 H100 V100 H0 Z', detail: 'M2 14 H98 M2 26 H98 M2 38 H98 M2 50 H98 M2 62 H98 M2 74 H98 M2 86 H98 M12 2 V98 M88 2 V98'}},
};

/** The rhythm a run is ruled at, which is the half of a run drawing both renderers share. */
export const RUN_DETAIL: Record<string, { across?: number[]; along?: number[] }> = {
  wall: {across: [0.2, 0.4, 0.6, 0.8]},
  counter: {along: [0.7, 0.86]},
  dock: {across: [0.14, 0.26, 0.38, 0.5, 0.62, 0.74, 0.86], along: [0.12, 0.88]},
};

/**
 * Which of an entry’s two drawings a footprint takes.
 *
 * The one whose orientation matches the footprint; a square keeps whichever was authored, so
 * nothing flips on the boundary between the two.
 */
export function orientationOf(entry: ArtEntry, width: number, height: number): ArtPaths {
  if (width === height) return entry[entry.authored];
  return width > height ? entry.wide : entry.tall;
}

/**
 * THE RULE THAT WAS STATED TWICE AND DISAGREED: which column governs the drawing.
 *
 * Scenery draws from its KIND — there is no shape picker on a dock and what one looks like is
 * not a decision anybody needs to make. Everything a guest sits at draws from its SHAPE. Null
 * means there is no silhouette and the place is a box with `cssRadius` corners.
 */
export function artKeyFor(kind: string, shape: string, assignable: boolean): string | null {
  if (!assignable) return DECOR_ART[kind] ? kind : null;
  return SHAPE_ART[shape] ? shape : null;
}

/** The entry a key names, wherever it lives. */
export function artEntry(key: string): ArtEntry | undefined {
  return DECOR_ART[key] ?? SHAPE_ART[key];
}
